import { useState } from "react";
import { useForm } from "@inertiajs/react";
import { Card, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import type { TrackEvent, GearItem } from "@/types/generated";
import { formatDistanceToNow } from "date-fns";
import { GearSelector } from "../GearSelector";
import LikeButton from "../LikeButton";
import { XIcon, PencilIcon } from "lucide-react";
import {
  PrivacyBadge,
  PrivacyLevel,
  PrivacySelect,
} from "../ui/privacy-select";

interface EventHeaderCardProps {
  trackEvent: TrackEvent;
  availableGear: GearItem[];
}

export function EventHeaderCard({
  trackEvent,
  availableGear,
}: EventHeaderCardProps) {
  const [isEditing, setIsEditing] = useState(false);

  const { data, setData, put, processing, reset } = useForm({
    title: trackEvent.title || "",
    description: trackEvent.description || "",
    privacy: (trackEvent.privacy as PrivacyLevel) || "friends_only",
    gear_item_ids: trackEvent.gear_items?.map((g) => g.uuid) || [],
  });

  const userName = `${trackEvent.user.first_name} ${trackEvent.user.last_name}`;
  const timestamp = trackEvent.session_started_at
    ? formatDistanceToNow(new Date(trackEvent.session_started_at), {
        addSuffix: true,
      })
    : null;

  function handleSave(e: React.FormEvent) {
    e.preventDefault();
    put(`/event/${trackEvent.id}`, {
      preserveUrl: true,
      onSuccess: () => {
        setIsEditing(false);
      },
    });
  }

  function handleCancel() {
    reset();
    setIsEditing(false);
  }

  return (
    <Card className="mb-6">
      <CardHeader className="gap-3">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="text-sm leading-normal">
              <span className="font-semibold">{userName}</span>
              {timestamp && (
                <span className="text-muted-foreground"> · {timestamp}</span>
              )}
            </div>
            {isEditing ? (
              <form onSubmit={handleSave} className="space-y-3 mt-3">
                <input
                  type="text"
                  value={data.title}
                  onChange={(e) => setData("title", e.target.value)}
                  className="w-full px-3 py-2 text-lg font-semibold border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="Event title"
                />
                <textarea
                  value={data.description}
                  onChange={(e) => setData("description", e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring font-mono text-sm"
                  placeholder="Description (Markdown supported)"
                />
                <div className="pt-2">
                  <PrivacySelect
                    label="Event Privacy"
                    description="Control who can view this event"
                    value={data.privacy}
                    onChange={(value) => setData("privacy", value)}
                    showDescription={false}
                  />
                </div>
                {availableGear && availableGear.length > 0 && (
                  <div className="pt-2">
                    <GearSelector
                      availableGear={availableGear}
                      selectedGearIds={data.gear_item_ids}
                      onChange={(gearIds) => setData("gear_item_ids", gearIds)}
                    />
                  </div>
                )}
                <div className="flex gap-2">
                  <Button type="submit" disabled={processing} size="sm">
                    {processing ? "Saving..." : "Save"}
                  </Button>
                  <Button
                    type="button"
                    onClick={handleCancel}
                    variant="ghost"
                    size="sm"
                  >
                    <XIcon className="w-4 h-4 mr-1" />
                    Cancel
                  </Button>
                </div>
              </form>
            ) : (
              <>
                <CardTitle className="leading-tight mt-1">
                  {trackEvent.title || "Untitled Session"}
                </CardTitle>
                {trackEvent.description && (
                  <CardDescription
                    className="leading-normal prose prose-sm max-w-none mt-2"
                    dangerouslySetInnerHTML={{
                      __html: trackEvent.description_html || "",
                    }}
                  />
                )}
              </>
            )}
          </div>
          {!isEditing && (
            <div className="flex items-center gap-2">
              {trackEvent.can_edit && (
                <Button
                  onClick={() => setIsEditing(true)}
                  variant="ghost"
                  size="sm"
                >
                  <PencilIcon className="w-4 h-4" />
                </Button>
              )}
              <LikeButton
                trackEventId={trackEvent.id}
                isLiked={trackEvent.liked_by_current_user}
                likesCount={trackEvent.likes_count}
              />
            </div>
          )}
        </div>
        {trackEvent.privacy && (
          <div className="scale-[0.85] origin-left opacity-70">
            <PrivacyBadge privacy={trackEvent.privacy as PrivacyLevel} />
          </div>
        )}
      </CardHeader>
    </Card>
  );
}
